// Shared presentational primitives for the dark customer visual
// system (see productTokens.js). Customer routes reuse
// these instead of hand-rolling cards/buttons/pills/empty-states with its
// own inline colors — that's how the app stays one coherent product instead
// of N independently-styled pages.
//
// Pure presentation only: no API calls, no business/financial logic. Money
// amounts and status wording are passed in as props by the caller, which
// must derive them from the shared state resolvers in transactionState.js /
// dashboardMoneyState.js — never invented here.
import { Product as D } from './productTokens';

// ---- Surfaces -----------------------------------------------------------

export function Card({ children, style, accentColor, ...rest }) {
  return (
    <section
      className="tt-card"
      style={{
        position: 'relative',
        border: `1px solid ${accentColor ? `${accentColor}40` : D.border}`,
        borderRadius: D.radius,
        background: D.surface,
        backdropFilter: 'blur(16px)',
        padding: 20,
        ...style,
      }}
      {...rest}
    >
      {children}
    </section>
  );
}

// ---- Buttons --------------------------------------------------------------
// Deliberately no color-tinted box-shadow on any variant — a neutral dark
// shadow only, so primary CTAs never read as a decorative neon glow.

const buttonBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  border: 'none',
  borderRadius: D.radiusSm,
  padding: '12px 20px',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontFamily: D.sans,
  transition: 'filter 0.12s, transform 0.08s',
};

export function PrimaryButton({ children, style, ...rest }) {
  return (
    <button
      className="tt-btn"
      style={{ ...buttonBase, background: D.accentGradient, color: '#061421', boxShadow: D.shadow, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function WarnButton({ children, style, ...rest }) {
  return (
    <button
      className="tt-btn"
      style={{ ...buttonBase, background: D.warn, color: D.warnText, boxShadow: D.shadow, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, style, ...rest }) {
  return (
    <button
      className="tt-btn"
      style={{ ...buttonBase, background: D.hover, color: D.text, border: `1px solid ${D.border}`, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, style, ...rest }) {
  return (
    <button
      className="tt-btn"
      style={{ ...buttonBase, background: 'transparent', color: D.accent, padding: '8px 4px', fontWeight: 600, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function DangerButton({ children, style, ...rest }) {
  return (
    <button
      className="tt-btn"
      style={{ ...buttonBase, background: 'transparent', color: D.error, border: `1px solid ${D.error}66`, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function semanticTone(color) {
  return ({ '#3DD194': '#3DD194', '#3FB950': '#3DD194', '#00FFA3': '#3DD194', '#00D1FF': '#2F9BFF', '#2F9BFF': '#2F9BFF', '#F5AB1F': '#F5AB1F', '#F0B429': '#F5AB1F', '#A78BFA': '#A78BFA', '#F85149': '#F0665C', '#F0665C': '#F0665C' })[String(color).toUpperCase()] || color;
}

// ---- Status pill ----------------------------------------------------------
// Color supports meaning (semantic state), never decoration. `color` should
// always come from a shared state resolver's own color field
// (resolveEscrowUiState().color, etc.) — never re-derived per page.

export function StatusPill({ label, color = D.sub, dot = true, style }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        color: semanticTone(color), background: `${color}18`, border: `1px solid ${color}66`,
        borderRadius: 999, padding: '5px 11px', fontSize: 12, fontWeight: 700,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />}
      {label}
    </span>
  );
}

// ---- Money amount -----------------------------------------------------
// Always render money as the most visually prominent element on a
// transaction/money screen — caller supplies the already-formatted string.

export function MoneyAmount({ amount, word, size = 'lg', style }) {
  const sizes = { lg: 'clamp(28px, 3.4vw, 36px)', md: 22, sm: 17 };
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', ...style }}>
      <span style={{ color: D.text, fontSize: sizes[size] || sizes.lg, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
        {amount}
      </span>
      {word && <span style={{ color: D.text, fontSize: size === 'lg' ? 'clamp(18px, 2vw, 22px)' : 15, fontWeight: 700 }}>{word}</span>}
    </div>
  );
}

// ---- Page header ------------------------------------------------------

export function PageHeader({ eyebrow, title, subtitle, action, style }) {
  return (
    <div className="tt-page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20, ...style }}>
      <div>
        {eyebrow && (
          <p style={{ margin: '0 0 4px', color: D.accent, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{eyebrow}</p>
        )}
        <h1 style={{ margin: 0, color: D.text, fontSize: 'clamp(22px, 2.6vw, 28px)', fontWeight: 700, letterSpacing: '-0.02em' }}>{title}</h1>
        {subtitle && <p style={{ margin: '6px 0 0', color: D.sub, fontSize: 14 }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ---- Empty / loading / error states ------------------------------------

export function EmptyState({ icon: Icon, title, body, action }) {
  return (
    <div style={{ minHeight: 180, display: 'grid', placeItems: 'center', textAlign: 'center', border: `1px dashed ${D.border}`, borderRadius: D.radius, padding: 32 }}>
      <div>
        {Icon && <Icon size={28} color={D.dim} style={{ margin: '0 auto 12px' }} />}
        {title && <p style={{ margin: '0 0 6px', color: D.text, fontSize: 14, fontWeight: 700 }}>{title}</p>}
        {body && <p style={{ margin: '0 0 16px', color: D.sub, fontSize: 13 }}>{body}</p>}
        {action}
      </div>
    </div>
  );
}

export function LoadingState({ label = 'Loading…' }) {
  return (
    <div style={{ minHeight: 180, display: 'grid', placeItems: 'center', color: D.sub, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            width: 16, height: 16, borderRadius: '50%',
            border: `2px solid ${D.border}`, borderTopColor: D.accent,
            animation: 'tt-spin 0.7s linear infinite',
          }}
        />
        {label}
      </div>
      <style>{'@keyframes tt-spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

export function ErrorState({ icon: Icon, title = 'Something went wrong', body, action }) {
  return (
    <div style={{ minHeight: 180, display: 'grid', placeItems: 'center', textAlign: 'center', border: `1px solid ${D.error}40`, background: `${D.error}0D`, borderRadius: D.radius, padding: 32 }}>
      <div>
        {Icon && <Icon size={28} color={D.error} style={{ margin: '0 auto 12px' }} />}
        <p style={{ margin: '0 0 6px', color: D.text, fontSize: 14, fontWeight: 700 }}>{title}</p>
        {body && <p style={{ margin: '0 0 16px', color: D.sub, fontSize: 13 }}>{body}</p>}
        {action}
      </div>
    </div>
  );
}

// ---- Form controls ------------------------------------------------------

export const inputStyle = {
  width: '100%',
  background: D.hover,
  border: `1px solid ${D.border}`,
  borderRadius: D.radiusSm,
  padding: '11px 14px',
  color: D.text,
  fontSize: 14,
  fontFamily: D.sans,
  outline: 'none',
};

export function FieldLabel({ children }) {
  return <label style={{ display: 'block', margin: '0 0 6px', color: D.sub, fontSize: 12.5, fontWeight: 600 }}>{children}</label>;
}

export function Input(props) {
  return <input {...props} className={`tt-input ${props.className || ''}`} style={{ ...inputStyle, ...(props.style || {}) }} />;
}

export function Textarea(props) {
  return <textarea {...props} className={`tt-input ${props.className || ''}`} style={{ ...inputStyle, resize: 'vertical', ...(props.style || {}) }} />;
}

// Shared CSS for the dark customer shell — import once per page via
// `<style>{customerUiGlobalCss}</style>` (or reuse the one Dashboard.js
// already injects, which covers the same selectors).
export const customerUiGlobalCss = `
  .tt-card { transition: border-color 0.15s; }
  .tt-btn { transition: filter 0.12s, border-color 0.12s, background 0.12s, transform 0.08s; }
  .tt-btn:hover:not(:disabled) { filter: brightness(1.08); }
  .tt-btn:active:not(:disabled) { transform: translateY(1px); }
  .tt-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .tt-input:focus { border-color: ${D.accent} !important; box-shadow: 0 0 0 2px ${D.accent}33; }
  .tt-row-hover:hover { background: ${D.hover} !important; }
`;
